import React, { useEffect, useState } from 'react';
import { Col, Input, Button, Typography, Divider, Skeleton, Image, DatePicker, Popconfirm, message } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, PaperClipOutlined } from "@ant-design/icons";
import constants from '../../config/constants';
import authStore from '../../store/authStore';
import moment from 'moment';
import Permissions from '../../config/permssions'
import issueTrackerServices from '../../services/issueTrackerServices';
import AttachmentUpload from '../issue-tracker-ticket-form/attachmentUpload';

const { Text } = Typography
const tealColor = '#4F8C92';

interface Props {
    issueDetails?: any,
    onResolveTicket: (values: any, action: any) => void;
    onIgnoreTicket: () => void;
    onCancel: () => void;
    onMarkAsMissed: () => void;
    imageList: any[],
    skeletonOn: boolean,
    imageCreatedBy: any[],
    imageNotes: any[],
    fetchIssueDetailsWithId: () => void;
}

const IssueTrackerDiagnosticDetails: React.FC<Props> = ({
    issueDetails,
    onResolveTicket,
    onCancel,
    onMarkAsMissed,
    imageList,
    skeletonOn,
    imageCreatedBy,
    fetchIssueDetailsWithId
}) => {
    const [editingNoteId, setEditingNoteId] = useState<string | null>(null)
    const [editDraft, setEditDraft] = useState<{ notes: string; created_on: any }>({ notes: '', created_on: null })
    const [newNote, setNewNote] = useState<{ notes: string; created_on: any }>({ notes: '', created_on: moment() })
    const [saving, setSaving] = useState(false)
    const [isAttachmentModalOpen, setIsAttachmentModalOpen] = useState(false)
    const [attachmentLoading, setAttachmentLoading] = useState(false)

    const orgId = issueDetails?.org_id
    const mongoId = issueDetails?._id
    const notesLocked = issueDetails?.status === 'RESOLVED' || issueDetails?.status === 'CLOSED' || issueDetails?.status === 'MISSED'
    const canEditNotes = !notesLocked && authStore?.currentUser?.access?.includes(Permissions.create_edit_attachment_issue_tracker)
    const canAddAttachments = !notesLocked && authStore?.currentUser?.access?.includes(Permissions.create_edit_attachment_issue_tracker)

    useEffect(() => {
        setEditingNoteId(null)
        setNewNote({ notes: '', created_on: moment() })
    }, [issueDetails?._id, issueDetails?.diagnosis_notes])

    const showLegacy = !!(issueDetails?.diagnosis || issueDetails?.overall_summary)

    const onAddNote = async () => {
        if (!mongoId || !newNote.notes?.trim()) {
            message.warning('Enter note text')
            return
        }
        setSaving(true)
        try {
            await issueTrackerServices.addDiagnosisNote(mongoId, {
                notes: newNote.notes.trim(),
                created_on: newNote.created_on ? moment(newNote.created_on).valueOf() : Date.now()
            }, orgId)
            message.success('Note added')
            setNewNote({ notes: '', created_on: moment() })
            fetchIssueDetailsWithId()
        } catch (e: any) {
            message.error(e?.response?.data?.message || 'Failed to add note')
        } finally {
            setSaving(false)
        }
    }

    const onSaveEdit = async () => {
        if (!mongoId || !editingNoteId) return
        setSaving(true)
        try {
            await issueTrackerServices.updateDiagnosisNote(mongoId, editingNoteId, {
                notes: editDraft.notes,
                created_on: editDraft.created_on ? moment(editDraft.created_on).valueOf() : undefined
            }, orgId)
            message.success('Note updated')
            setEditingNoteId(null)
            fetchIssueDetailsWithId()
        } catch (e: any) {
            message.error(e?.response?.data?.message || 'Failed to update note')
        } finally {
            setSaving(false)
        }
    }

    const onDeleteNote = async (noteId: string) => {
        if (!mongoId) return
        setSaving(true)
        try {
            await issueTrackerServices.deleteDiagnosisNote(mongoId, noteId, orgId)
            message.success('Note removed')
            fetchIssueDetailsWithId()
        } catch (e: any) {
            message.error(e?.response?.data?.message || 'Failed to delete note')
        } finally {
            setSaving(false)
        }
    }

    const startEdit = (row: any) => {
        setEditingNoteId(row.id)
        setEditDraft({
            notes: row.notes || '',
            created_on: row.created_on ? moment(row.created_on) : moment()
        })
    }

    const onResolve = () => {
        onResolveTicket({}, "resolve")
    };

    const showAttachments = () => {
        if (skeletonOn) {
            return (
                <Col>
                    <Skeleton.Avatar active className="mr-3" />
                    <Skeleton.Input active style={{ width: 250, maxWidth: 250, minWidth: 200 }} className="mr-1 mb-1" />
                    <Skeleton.Input active style={{ width: 250, maxWidth: 250, minWidth: 200 }} />
                </Col>
            );
        }
        if (!imageList?.length) return null;
        return (
            <>
                {imageList.map((row: any, rowIndex: number) => {
                    if (!row?.length) return null;
                    return (
                        <div key={rowIndex} className='mb-3'>
                            <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', marginBottom: 5 }}>
                                <div style={{ display: 'flex', alignItems: 'center', marginRight: 10 }}>
                                    <div className="avatar" style={{ marginRight: 25, fontSize: 15 }}>
                                        {imageCreatedBy[rowIndex]?.createdBy ? (imageCreatedBy[rowIndex]?.createdBy?.charAt(0) + imageCreatedBy[rowIndex]?.createdBy?.slice(-1))?.toUpperCase() : 'U'}
                                    </div>
                                    <div>
                                        <p></p>
                                        <p style={{ fontSize: 12 }}>
                                            Uploaded on {moment(imageCreatedBy[rowIndex]?.createdAt).format("DD-MMMM-YYYY - HH:mm:ss") !==
                                                "Invalid Date"
                                                ? moment(imageCreatedBy[rowIndex]?.createdAt).format("DD-MMMM-YYYY - HH:mm:ss")
                                                : "-"}
                                            {imageCreatedBy[rowIndex]?.createdBy && (imageCreatedBy[rowIndex]?.createdBy !== authStore.currentUser?.name) ? ` by ${imageCreatedBy[rowIndex]?.createdBy} ` : ''}
                                        </p>
                                    </div>
                                </div>
                            </div>
                            <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', marginLeft: 70 }}>
                                {row?.map((image: any, colIndex: number) => (
                                    <div key={colIndex} style={{ marginRight: 10 }}>
                                        <Image src={image} style={{ height: 120, padding: 20 }} />
                                    </div>
                                ))}
                            </div>
                        </div>
                    );
                })}
            </>
        );
    };

    const diagnosisNotes = Array.isArray(issueDetails?.diagnosis_notes) ? issueDetails.diagnosis_notes : []

    const notesSection = (
        <>
            <div className="d-flex align-items-center justify-content-between mb-3">
                <p style={{ fontSize: "16px", fontWeight: 500, marginBottom: 0 }}>
                    Diagnosis Notes
                </p>
                {canAddAttachments && (
                    <Button
                        type="default"
                        className="h-36 py-0"
                        onClick={() => setIsAttachmentModalOpen(true)}
                        style={{
                            borderColor: tealColor,
                            color: tealColor,
                            backgroundColor: '#fff',
                            borderRadius: 10,
                            fontWeight: 500,
                            padding: '0 24px',
                            height: '36px'
                        }}
                    >
                        <PaperClipOutlined /> Add Attachments
                    </Button>
                )}
            </div>
            {/* {showAttachments()} */}

            <div className="table-responsive">
                <table className="table" style={{ tableLayout: 'fixed', width: '100%' }}>
                    <colgroup>
                        <col style={{ width: '20%' }} />
                        <col style={{ width: canEditNotes ? '66%' : '80%' }} />
                        {canEditNotes && <col style={{ width: '14%' }} />}
                    </colgroup>
                    <thead style={{ backgroundColor: tealColor, color: 'white' }}>
                        <tr>
                            <th style={{ paddingLeft: 5, paddingRight: 0 }}>Created Date</th>
                            <th style={{ paddingLeft: 5, paddingRight: 0 }}>Notes</th>
                            {canEditNotes && <th style={{ paddingLeft: 5, paddingRight: 0 }}>Action</th>}
                        </tr>
                    </thead>
                    <tbody>
                        {canEditNotes && (
                            <tr>
                                <td style={{ paddingLeft: 5, paddingRight: 0, verticalAlign: 'middle' }}>
                                    <DatePicker
                                        className="w-100"
                                        showTime
                                        format="YYYY-MM-DD HH:mm"
                                        value={newNote.created_on}
                                        onChange={(v) => setNewNote((n) => ({ ...n, created_on: v }))}
                                    />
                                </td>
                                <td style={{ paddingLeft: 5, paddingRight: 0, verticalAlign: 'middle' }}>
                                    <Input.TextArea
                                        rows={2}
                                        placeholder="Enter new note..."
                                        value={newNote.notes}
                                        onChange={(e) => setNewNote((n) => ({ ...n, notes: e.target.value }))}
                                        maxLength={400}
                                    />
                                </td>
                                <td style={{ paddingLeft: 5, paddingRight: 0, verticalAlign: 'middle' }}>
                                    <Button
                                        type="primary"
                                        icon={<PlusOutlined />}
                                        onClick={onAddNote}
                                        loading={saving}
                                        style={{ backgroundColor: tealColor, borderColor: tealColor, width: '100%' }}
                                    >
                                        Add
                                    </Button>
                                </td>
                            </tr>
                        )}
                        {diagnosisNotes.length === 0 ? (
                            <tr>
                                <td style={{ paddingLeft: 0, paddingRight: 0 }} colSpan={canEditNotes ? 3 : 2} className="text-center">No diagnosis notes yet</td>
                            </tr>
                        ) : diagnosisNotes.map((row: any) => (
                            <tr key={row.id}>
                                <td style={{ paddingLeft: 5, paddingRight: 0, verticalAlign: 'middle' }}>
                                    {editingNoteId === row.id ? (
                                        <DatePicker
                                            className="w-100"
                                            showTime
                                            format="YYYY-MM-DD HH:mm"
                                            value={editDraft.created_on}
                                            onChange={(v) => setEditDraft((d) => ({ ...d, created_on: v }))}
                                        />
                                    ) : (
                                        row.created_on ? moment(row.created_on).format('MM/DD/YYYY HH:mm:ss') : '--'
                                    )}
                                </td>
                                <td style={{ paddingLeft: 5, paddingRight: 0, verticalAlign: 'middle' }}>
                                    {editingNoteId === row.id ? (
                                        <Input.TextArea
                                            rows={2}
                                            value={editDraft.notes}
                                            onChange={(e) => setEditDraft((d) => ({ ...d, notes: e.target.value }))}
                                            maxLength={400}
                                        />
                                    ) : (
                                        <span style={{ whiteSpace: 'pre-wrap' }}>{row.notes || '--'}</span>
                                    )}
                                </td>
                                {canEditNotes && (
                                    <td style={{ paddingLeft: 5, paddingRight: 0, verticalAlign: 'middle' }}>
                                        {editingNoteId === row.id ? (
                                            <>
                                                <Button type="link" size="small" onClick={onSaveEdit} loading={saving}>Save</Button>
                                                <Button type="link" size="small" onClick={() => setEditingNoteId(null)}>Cancel</Button>
                                            </>
                                        ) : (
                                            <>
                                                <Button type="link" icon={<EditOutlined />} onClick={() => startEdit(row)} />
                                                <Popconfirm title="Delete this note?" onConfirm={() => onDeleteNote(row.id)}>
                                                    <Button type="link" danger icon={<DeleteOutlined />} />
                                                </Popconfirm>
                                            </>
                                        )}
                                    </td>
                                )}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </>
    )

    const legacyBlock = showLegacy && (
        <div className="mb-4 p-3" style={{ background: '#fafafa', borderRadius: 8 }}>
            <p style={{ fontSize: "16px", fontWeight: 500 }} className="mb-2">Diagnosis Summary</p>
            {issueDetails?.diagnosis ? (
                <div className="mb-3">
                    <Text type="secondary" className="d-block mb-1">Diagnosis</Text>
                    <Text className="d-block" style={{ whiteSpace: 'pre-wrap' }}>{issueDetails.diagnosis}</Text>
                </div>
            ) : null}
            {issueDetails?.overall_summary ? (
                <div>
                    <Text type="secondary" className="d-block mb-1">Overall summary</Text>
                    <Text className="d-block" style={{ whiteSpace: 'pre-wrap' }}>{issueDetails.overall_summary}</Text>
                </div>
            ) : null}
        </div>
    )

    return (
        <>
            {/* <div style={{ display: 'flex', alignItems: 'center', justifyContent: "space-between" }} className='mb-3'>
                <p style={{ fontSize: "16px", fontWeight: 500, marginBottom: 0 }} className='mr-2'>
                    Diagnosis Details
                </p>
            </div> */}

            {legacyBlock}
            {notesSection}

            {issueDetails?.status === "OPEN" &&
                <>
                    <Divider type="horizontal" className="mt-3 mb-4 invisible" />
                    <Col span={24}>
                        <div className="text-right mt-4">
                            <Button className="text-left" type="text" onClick={onCancel}>Cancel</Button>
                            {authStore?.isOrgAdmin() && (
                                <Button
                                    className="h-36 py-0 pl-4 pr-4 fs-14 mr-2"
                                    type="default"
                                    onClick={onMarkAsMissed}
                                >
                                    Mark as Missed
                                </Button>
                            )}
                            {(authStore?.isOrgAdmin() || authStore?.currentUser?.active_role?.role_type === constants.role['Field Technician']) &&
                                <Button className="h-36 py-0 pl-4 pr-4 fs-14 ins_bg_clr border-0 font-weight-bold issue-ticket-save-btn" type="primary" onClick={() => onResolveTicket({}, "resolve")}>
                                    Submit & Complete Diagnosis
                                </Button>}
                        </div>
                    </Col>
                </>
            }
            {issueDetails?.status === "PENDING REVIEW" && authStore?.isOrgAdmin() &&
                <>
                    <Divider type="horizontal" className="mt-3 mb-4 invisible" />
                    <Col span={24}>
                        <div className="text-right mt-4">
                            <Button className="text-left" type="text" onClick={onCancel}>Cancel</Button>
                            <Button
                                className="h-36 py-0 pl-4 pr-4 fs-14 mr-2"
                                type="default"
                                onClick={onMarkAsMissed}
                            >
                                Mark as Missed
                            </Button>
                            <Button className="h-36 py-0 pl-4 pr-4 fs-14 ins_bg_clr border-0 font-weight-bold issue-ticket-save-btn" type="primary" onClick={onResolve}>Resolve Ticket</Button>
                        </div>
                    </Col>
                </>
            }
            {issueDetails?.status === "INCOMPLETE" && authStore?.isOrgAdmin() &&
                <>
                    <Divider type="horizontal" className="mt-3 mb-4 invisible" />
                    <Col span={24}>
                        <div className="text-right mt-4">
                            <Button className="text-left" type="text" onClick={onCancel}>Cancel</Button>
                            <Button className="h-36 py-0 pl-4 pr-4 fs-14 ins_bg_clr border-0 font-weight-bold issue-ticket-save-btn" type="primary" onClick={onResolve}>Create Sub Ticket</Button>
                        </div>
                    </Col>
                </>
            }
            <AttachmentUpload
                isModalOpen={isAttachmentModalOpen}
                setIsModalOpen={setIsAttachmentModalOpen}
                ticketId={issueDetails?.issue_id || null}
                setLoading={setAttachmentLoading}
                loading={attachmentLoading}
                fetchIssueDetailsWithId={fetchIssueDetailsWithId}
            />
        </>
    );
}

export default IssueTrackerDiagnosticDetails;
